/**
 * Invite accept page - shown when user lands via redirect from backend with error,
 * or when they need to log in first. The primary accept flow is: user clicks link
 * -> SPA /invites/accept?token=... -> POST /api/invites/accept with Bearer JWT.
 */

import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BRAND } from '@/lib/config/brand'
import { useAuth } from '@/providers/auth-provider'
import { useWorkspace } from '@/providers/workspace-provider'
import { fetchWithAuth } from '@/lib/api'

const ERROR_MESSAGES: Record<string, string> = {
  missing_token: 'Invalid invite link. The token is missing.',
  invalid_token: 'Invalid or expired invite link.',
  already_used: 'This invite has already been used.',
  expired: 'This invite has expired.',
  invalid_user: 'Unable to verify your account.',
  email_mismatch: 'This invite was sent to a different email address. Please sign in with that account.',
  server_error: 'Something went wrong. Please try again.',
}

type AcceptErrorPayload = {
  code?: string
  error?: string
  invite_email?: string
  redirect_to_login?: boolean
}

function errorQueryFromPayload(data: AcceptErrorPayload): string {
  const code = data.code || 'server_error'
  const params = new URLSearchParams({ error: code })
  if (code === 'email_mismatch' && data.invite_email) {
    params.set('invite_email', data.invite_email)
  }
  return params.toString()
}

export function InviteAcceptPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { session } = useAuth()
  const { refetchWorkspaces, hideOtherWorkspaces } = useWorkspace()
  const isAuthenticated = !!session
  const error = searchParams.get('error')
  const token = searchParams.get('token')
  const inviteEmail = searchParams.get('invite_email')

  useEffect(() => {
    if (!token || !isAuthenticated || error) return

    let cancelled = false

    ;(async () => {
      try {
        const res = await fetchWithAuth('/api/invites/accept', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
        if (cancelled) return

        const data = (await res.json().catch(() => ({}))) as AcceptErrorPayload & {
          success?: boolean
          organization_id?: string
        }

        if (res.ok && data.success && data.organization_id) {
          await refetchWorkspaces()
          if (cancelled) return
          hideOtherWorkspaces(data.organization_id)
          navigate('/', { replace: true })
          return
        }

        if (data.code === 'already_used') {
          await refetchWorkspaces()
          if (cancelled) return
          navigate('/', { replace: true })
          return
        }

        if (res.status === 401 || data.redirect_to_login) {
          navigate(`/login?redirect=${encodeURIComponent(`/invites/accept?token=${token}`)}`, {
            replace: true,
          })
          return
        }

        navigate(`/invites/accept?${errorQueryFromPayload(data)}`, { replace: true })
      } catch {
        if (!cancelled) {
          navigate(`/invites/accept?error=server_error`, { replace: true })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token, isAuthenticated, error, navigate, refetchWorkspaces, hideOtherWorkspaces])

  if (error) {
    const message = ERROR_MESSAGES[error] || 'An error occurred.'
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h1 className="text-xl font-semibold">Invite problem</h1>
          <p className="text-muted-foreground">{message}</p>
          {error === 'email_mismatch' && inviteEmail && (
            <p className="text-sm text-muted-foreground">Invite was sent to: {inviteEmail}</p>
          )}
          <Button onClick={() => navigate('/')}>Go to {BRAND.displayName}</Button>
        </div>
      </div>
    )
  }

  if (token && !isAuthenticated) {
    const redirectUrl = `/invites/accept?token=${token}`
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-xl font-semibold">Accept workspace invite</h1>
          <p className="text-muted-foreground">Please sign in to accept this invitation.</p>
          <Button onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`)}>
            Sign in
          </Button>
          <Button variant="ghost" onClick={() => navigate('/')}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center space-y-4">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto" />
        <p className="text-muted-foreground">Accepting invite...</p>
      </div>
    </div>
  )
}
